import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { DealStatus } from '@/config/dealStatus';
import { DealTemperature } from '@/config/temperatureConfig';
import { useAuthStore } from '@/stores/authStore';
import { useEmployeePermissions } from './useEmployeePermissions';
import { ProductCurrency } from '@/types/products';

export interface Deal {
  id: number;
  title: string;
  description?: string;
  amount: number;
  currency: ProductCurrency;
  probability: number;
  expected_close_date?: string;
  actual_close_date?: string;
  status: DealStatus;
  stage_id: number;
  stage_name?: string;
  pipeline_id: number;
  pipeline_name?: string;
  company_id?: number;
  company_name?: string;
  contact_id?: number;
  contact_name?: string;
  assigned_to: number;
  assigned_to_name?: string;
  created_by: number;
  group_admin_id?: number;
  created_at: string;
  updated_at: string;
  // Nouveaux champs Phase 1
  temperature?: DealTemperature;
  score?: number;
  last_score_update?: string;
  // Prestations associées (noms), renvoyées par /deals/list
  prestation_names?: string[];
  // Localisation (dénormalisée sur le deal)
  country?: string;
  city?: string;
  postal_code?: string;
  // Relations enrichies
  contact?: {
    id: number;
    first_name: string;
    last_name: string;
    email?: string;
    phone?: string;
    whatsapp?: string;
    position?: string;
    linkedin_url?: string;
    instagram_url?: string;
    facebook_url?: string;
    twitter_url?: string;
    avatar_url?: string;
  };
  company?: {
    id: number;
    name: string;
    industry?: string;
    website?: string;
    phone?: string;
    email?: string;
    address?: string;
    city?: string;
    country?: string;
    postal_code?: string;
    linkedin_url?: string;
    facebook_url?: string;
    twitter_url?: string;
    logo_url?: string;
  };
}

export interface DealsResponse {
  items: Deal[];
  total: number;
  page: number;
  pages: number;
  per_page: number;
}

export interface DealFilters {
  search?: string;
  pipeline_id?: number;
  stage_id?: number;
  status?: string;
  assigned_to?: number;
  page?: number;
  per_page?: number;
  // Prestation-based filtering
  prestation_ids?: number[];
  prestation_match?: 'any' | 'all';
  // Location filters (company/contact)
  country?: string;
  city?: string;
  postal_code?: string;
}

export interface DealStats {
  total_deals: number;
  total_value: number;
  won_deals: number;
  lost_deals: number;
  open_deals: number;
  win_rate: number;
  average_deal_size: number;
}

export interface DealForecast {
  period_months: number;
  total_deals: number;
  total_value: number;
  forecast_value: number;
  weighted_forecast: number;
  best_case: number;
  worst_case: number;
}

export interface PipelineStage {
  id: number;
  name: string;
  order: number;
  probability: number;
  deals: Deal[];
  total_value: number;
  deals_count: number;
}

export interface Pipeline {
  id: number;
  name: string;
  description?: string;
  is_active?: boolean;
  is_default?: boolean;
  created_by?: number | null;
}

export interface PipelineBoard {
  pipeline: Pipeline;
  stages: PipelineStage[];
}

// Lightweight stage type for selectors
export interface Stage {
  id: number;
  name: string;
  order: number;
  probability: number;
  pipeline_id: number;
}

// Hook pour récupérer la liste des pipelines de l'utilisateur
export const usePipelines = () => {
  const userId = useAuthStore((s) => s.user?.id);
  const isAuthed = useAuthStore((s) => !!s.token);
  return useQuery({
    queryKey: ['deals', 'pipelines', userId],
    queryFn: async (): Promise<Pipeline[]> => {
      const response = await api.get('/deals/pipelines');
      return response.data;
    },
    enabled: !!isAuthed,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook pour récupérer les stages d'un pipeline
export const usePipelineStages = (pipelineId?: number) => {
  const userId = useAuthStore((s) => s.user?.id);
  const isAuthed = useAuthStore((s) => !!s.token);
  return useQuery({
    queryKey: ['deals', 'pipelines', userId, pipelineId, 'stages'],
    queryFn: async (): Promise<Stage[]> => {
      const response = await api.get(`/deals/pipelines/${pipelineId}/stages`);
      return response.data;
    },
    enabled: !!isAuthed && !!pipelineId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Hook pour créer un pipeline
export const useCreatePipeline = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, description, is_global }: { name: string; description?: string; is_global?: boolean }): Promise<Pipeline> => {
      const response = await api.post('/deals/pipelines', { name, description, is_global });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['deals', 'pipelines']);
    },
  });
};

// Hook to delete a pipeline
export const useDeletePipeline = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.delete(`/deals/pipelines/${id}`);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate pipelines and related caches
      queryClient.invalidateQueries(['deals', 'pipelines']);
      queryClient.invalidateQueries(['deals']);
      // Clear persisted pipeline selections
      try {
        localStorage.removeItem('crm_default_pipeline_id');
        localStorage.removeItem('crm_last_pipeline_id');
      } catch {}
    },
  });
};

// Hook to bulk delete pipelines
export const useBulkDeletePipelines = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: number[]) => {
      const response = await api.post('/deals/pipelines/bulk-delete', { ids });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['deals', 'pipelines']);
      queryClient.invalidateQueries(['deals']);
      try {
        localStorage.removeItem('crm_default_pipeline_id');
        localStorage.removeItem('crm_last_pipeline_id');
      } catch {}
    },
  });
};

// Types pour la création et la mise à jour qui correspondent au backend
export interface DealCreatePayload {
  title: string;
  description?: string | null;
  amount?: number | null;
  manual_amount?: number | null;
  currency: ProductCurrency;
  contact_id?: number | null;
  company_id?: number | null;
  pipeline_id: number;
  probability?: number | null;
  expected_close_date?: string | null; // Format YYYY-MM-DD
  temperature?: DealTemperature;
  // Location (optional, denormalized on deal)
  country?: string | null;
  city?: string | null;
  postal_code?: string | null;
}

export interface DealUpdatePayload {
  title?: string;
  description?: string | null;
  amount?: number | null;
  manual_amount?: number | null;
  contact_id?: number | null;
  company_id?: number | null;
  probability?: number | null;
  temperature?: DealTemperature;
  expected_close_date?: string | null; // Format YYYY-MM-DD
  // Location updates
  country?: string | null;
  city?: string | null;
  postal_code?: string | null;
}

// Hook pour récupérer la liste des deals
export const useDeals = (filters: DealFilters = {}, options?: { enabled?: boolean }) => {
  const userId = useAuthStore((s) => s.user?.id);
  const user = useAuthStore((s) => s.user);
  const isAuthed = useAuthStore((s) => !!s.token);
  const { hasModuleAccess } = useEmployeePermissions();
  
  // Vérifier si l'utilisateur a accès au module
  const canAccess = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'apporteur' || hasModuleAccess('deals');
  
  return useQuery({
    queryKey: ['deals', 'v2-prestations', { userId, ...filters }],
    queryFn: async (): Promise<Deal[]> => {
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.pipeline_id) params.append('pipeline_id', filters.pipeline_id.toString());
      if (filters.stage_id) params.append('stage_id', filters.stage_id.toString());
      if (filters.status) params.append('status', filters.status);
      if (filters.assigned_to) params.append('assigned_to', filters.assigned_to.toString());
      // Location filters
      if (filters.country) params.append('country', filters.country);
      if (filters.city) params.append('city', filters.city);
      if (filters.postal_code) params.append('postal_code', filters.postal_code);
      // Prestation filters
      if (filters.prestation_ids && Array.isArray(filters.prestation_ids)) {
        for (const id of filters.prestation_ids) {
          if (typeof id === 'number' && !Number.isNaN(id)) {
            params.append('prestation_ids', String(id));
          }
        }
      }
      if (filters.prestation_match) params.append('prestation_match', filters.prestation_match);
      if (filters.page) params.append('skip', ((filters.page - 1) * (filters.per_page || 20)).toString());
      if (filters.per_page) params.append('limit', filters.per_page.toString());
      
      const response = await api.get(`/deals/list?${params.toString()}`);
      // Dev-only logging: sample deals to validate location fields
      if (import.meta && import.meta.env && import.meta.env.DEV) {
        try {
          const sample = Array.isArray(response.data)
            ? response.data.slice(0, 3).map((d: any) => ({
                id: d?.id,
                country: d?.country,
                city: d?.city,
                postal_code: d?.postal_code,
              }))
            : response.data;
          console.debug('[useDeals] fetched deals sample', sample);
        } catch {}
      }
      return response.data;
    },
    enabled: canAccess && !!isAuthed && (options?.enabled ?? true),
    refetchOnMount: 'always',
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook pour infinite scroll des deals
export const useInfiniteDeals = (
  filters: DealFilters = {},
  options?: { pageSize?: number }
) => {
  const userId = useAuthStore((s) => s.user?.id);
  const isAuthed = useAuthStore((s) => !!s.token);
  const perPageDesired = options?.pageSize ?? Math.max(12, Math.min(48, filters.per_page || 24));
  const pageSize = Math.min(Math.max(perPageDesired, 1), 100);
  
  return useInfiniteQuery(
    ['deals-infinite', { userId, ...(filters || {}), pageSize }],
    async ({ pageParam }): Promise<Deal[]> => {
      const skip = typeof pageParam === 'number' && !isNaN(pageParam) ? pageParam : 0;
      const params = new URLSearchParams();
      
      // Pagination
      params.append('skip', skip.toString());
      params.append('limit', pageSize.toString());
      
      // Filters
      if (filters.search) params.append('search', filters.search);
      if (filters.pipeline_id) params.append('pipeline_id', filters.pipeline_id.toString());
      if (filters.stage_id) params.append('stage_id', filters.stage_id.toString());
      if (filters.status) params.append('status', filters.status);
      if (filters.assigned_to) params.append('assigned_to', filters.assigned_to.toString());
      if (filters.prestation_ids?.length) {
        filters.prestation_ids.forEach(pid => params.append('prestation_ids', pid.toString()));
      }
      if (filters.prestation_match) params.append('prestation_match', filters.prestation_match);
      if (filters.country) params.append('country', filters.country);
      if (filters.city) params.append('city', filters.city);
      if (filters.postal_code) params.append('postal_code', filters.postal_code);
      
      const response = await api.get(`/deals/list?${params.toString()}`);
      return response.data as Deal[];
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        if (!Array.isArray(lastPage)) return undefined;
        if (lastPage.length < pageSize) return undefined;
        const loaded = allPages.reduce((acc, p) => acc + (Array.isArray(p) ? p.length : 0), 0);
        return loaded;
      },
      staleTime: 2 * 60 * 1000,
      enabled: !!isAuthed,
    }
  );
};

// Hook pour récupérer un deal par ID
export const useDeal = (id: number) => {
  const userId = useAuthStore((s) => s.user?.id);
  const isAuthed = useAuthStore((s) => !!s.token);
  return useQuery({
    queryKey: ['deal', userId, id],
    queryFn: async () => {
      const response = await api.get(`/deals/${id}`);
      return response.data;
    },
    enabled: !!isAuthed && !!id,
  });
};

// Hook pour rechercher des deals
export const useSearchDeals = (query: string, options?: { enabled?: boolean }) => {
  const userId = useAuthStore((s) => s.user?.id);
  const isAuthed = useAuthStore((s) => !!s.token);
  return useQuery({
    queryKey: ['deals', 'search', userId, query],
    queryFn: async (): Promise<DealsResponse> => {
      const response = await api.get(`/deals/search?query=${encodeURIComponent(query)}`);
      return response.data;
    },
    enabled: !!isAuthed && query.length >= 2 && (options?.enabled !== false),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Hook pour les statistiques des deals
export const useDealsStats = (pipelineId?: number) => {
  const userId = useAuthStore((s) => s.user?.id);
  const isAuthed = useAuthStore((s) => !!s.token);
  return useQuery({
    queryKey: ['deals', 'stats', userId, pipelineId],
    queryFn: async (): Promise<DealStats> => {
      const params = pipelineId ? `?pipeline_id=${pipelineId}` : '';
      const response = await api.get(`/deals/stats${params}`);
      return response.data;
    },
    enabled: !!isAuthed,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook pour les prévisions de ventes
export const useSalesForecast = (pipelineId?: number, periodMonths: number = 3) => {
  const userId = useAuthStore((s) => s.user?.id);
  const isAuthed = useAuthStore((s) => !!s.token);
  return useQuery({
    queryKey: ['deals', 'forecast', userId, pipelineId, periodMonths],
    queryFn: async (): Promise<DealForecast> => {
      const params = new URLSearchParams();
      if (pipelineId) params.append('pipeline_id', pipelineId.toString());
      params.append('period_months', periodMonths.toString());
      
      const response = await api.get(`/deals/forecast?${params.toString()}`);
      return response.data;
    },
    enabled: !!isAuthed,
    staleTime: 15 * 60 * 1000, // 15 minutes
  });
};

// Hook pour la vue Kanban du pipeline
export const usePipelineBoard = (pipelineId?: number) => {
  const userId = useAuthStore((s) => s.user?.id);
  const isAuthed = useAuthStore((s) => !!s.token);
  return useQuery({
    queryKey: ['deals', 'pipeline', userId, pipelineId, 'board'],
    queryFn: async (): Promise<PipelineBoard> => {
      const response = await api.get(`/deals/pipeline/${pipelineId}/board`);
      return response.data;
    },
    enabled: !!isAuthed && !!pipelineId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Hook pour créer un deal
export const useCreateDeal = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (dealData: DealCreatePayload) => {
      const response = await api.post('/deals/create', dealData);
      return response.data;
    },
    onSuccess: () => {
      // Invalider les caches liés aux deals
      queryClient.invalidateQueries(['deals']);
    },
  });
};

// Hook pour mettre à jour un deal
export const useUpdateDeal = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: DealUpdatePayload }) => {
      const response = await api.put(`/deals/${id}`, data);
      return response.data;
    },
    onSuccess: (data: Deal, variables: { id: number; data: DealUpdatePayload }) => {
      // Mettre à jour le cache du deal spécifique
      queryClient.setQueryData(['deal', variables.id], data);
      // Invalider la liste des deals et les boards
      queryClient.invalidateQueries(['deals']);
    },
  });
};

// Hook pour supprimer un deal
export const useDeleteDeal = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.delete(`/deals/${id}`);
      return response.data;
    },
    onSuccess: (_: any, id: number) => {
      // Supprimer du cache
      queryClient.removeQueries(['deal', id]);
      // Invalider la liste des deals
      queryClient.invalidateQueries(['deals']);
    },
  });
};

// Hook pour déplacer un deal vers une autre étape
export const useMoveDeal = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ dealId, stageId }: { dealId: number; stageId: number }) => {
      const response = await api.post(`/deals/${dealId}/move`, { stage_id: stageId });
      return response.data;
    },
    // Optimistic update: move the deal in any cached pipeline board(s)
    onMutate: async ({ dealId, stageId }: { dealId: number; stageId: number }) => {
      // Cancel outgoing refetches so we don't overwrite our optimistic update
      await queryClient.cancelQueries(['deals']);

      // Snapshot previous boards to enable rollback
      const previousBoards = queryClient.getQueriesData<PipelineBoard>({ queryKey: ['deals', 'pipeline'] });

      // Apply optimistic update to any board containing the deal
      previousBoards.forEach(([key, board]) => {
        if (!board) return;
        // Locate the source stage containing the deal
        const sourceStage = board.stages.find((s) => s.deals?.some((d) => d.id === dealId));
        if (!sourceStage) return;

        const deal = sourceStage.deals.find((d) => d.id === dealId);
        if (!deal) return;

        // Prepare new stages array with the deal removed from source and added to destination (if present)
        const newStages = board.stages.map((s) => {
          if (s.id === sourceStage.id) {
            // Remove from source
            const filteredDeals = s.deals.filter((d) => d.id !== dealId);
            return {
              ...s,
              deals: filteredDeals,
              deals_count: Math.max(0, (s.deals_count || filteredDeals.length) - 1),
              total_value: (s.total_value || 0) - (deal.amount || 0),
            };
          }
          if (s.id === stageId) {
            // Add to destination (top of the list)
            return {
              ...s,
              deals: [{ ...deal, stage_id: stageId }, ...(s.deals || [])],
              deals_count: (s.deals_count || (s.deals?.length ?? 0)) + 1,
              total_value: (s.total_value || 0) + (deal.amount || 0),
            };
          }
          return s;
        });

        queryClient.setQueryData(key as any, { ...board, stages: newStages });
      });

      return { previousBoards } as { previousBoards: ReturnType<typeof queryClient.getQueriesData<PipelineBoard>> };
    },
    onError: (_err, _variables, context) => {
      // Rollback to previous state if mutation fails
      context?.previousBoards?.forEach(([key, data]) => {
        queryClient.setQueryData(key as any, data);
      });
    },
    onSuccess: (data: Deal, variables: { dealId: number; stageId: number }) => {
      // Update the specific deal cache with server response
      queryClient.setQueryData(['deal', variables.dealId], data);
    },
    onSettled: () => {
      // Ensure server state is synced
      queryClient.invalidateQueries(['deals']);
    },
  });
};

// Hook pour déplacer un deal vers un autre pipeline (avec stage optionnel)
export const useMoveDealToPipeline = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ dealId, pipelineId, stageId }: { dealId: number; pipelineId: number; stageId?: number }) => {
      const response = await api.post(`/deals/${dealId}/move_to_pipeline`, { pipeline_id: pipelineId, stage_id: stageId });
      return response.data;
    },
    onSuccess: (data: Deal, variables: { dealId: number; pipelineId: number; stageId?: number }) => {
      // Mettre à jour le cache du deal
      queryClient.setQueryData(['deal', variables.dealId], data);
      // Invalider les boards et la liste des deals
      queryClient.invalidateQueries(['deals']);
    },
  });
};

// Hook pour marquer un deal comme gagné
export const useMarkDealAsWon = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (dealId: number) => {
      // 1) Fetch current deal to get pipeline_id
      const dealResp = await api.get(`/deals/${dealId}`);
      const pipelineId = dealResp.data.pipeline_id as number;
      // 2) Fetch stages for that pipeline
      const stagesResp = await api.get(`/deals/pipelines/${pipelineId}/stages`);
      const stages: Stage[] = stagesResp.data;
      // 3) Find the 'won' stage by name (support FR) or fallback to probability 100
      const lower = (s: string | undefined) => (s || '').toLowerCase();
      const wonStage = stages.find(s => ['won', 'gagné'].includes(lower(s.name)))
        || stages.find(s => Math.round(s.probability) === 100);
      if (!wonStage) {
        throw new Error('Aucune étape "won" trouvée pour ce pipeline');
      }
      // 4) Move the deal to the won stage (backend will derive status + probability)
      const moveResp = await api.post(`/deals/${dealId}/move`, { stage_id: wonStage.id });
      return moveResp.data;
    },
    onSuccess: (data: Deal, dealId: number) => {
      // Mettre à jour le cache du deal
      queryClient.setQueryData(['deal', dealId], data);
      // Invalider les boards et la liste des deals
      queryClient.invalidateQueries(['deals']);
    },
  });
};

// Hook pour marquer un deal comme perdu
export const useMarkDealAsLost = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (dealId: number) => {
      // 1) Fetch current deal to get pipeline_id
      const dealResp = await api.get(`/deals/${dealId}`);
      const pipelineId = dealResp.data.pipeline_id as number;
      // 2) Fetch stages for that pipeline
      const stagesResp = await api.get(`/deals/pipelines/${pipelineId}/stages`);
      const stages: Stage[] = stagesResp.data;
      // 3) Find the 'lost' stage by name (support FR) or fallback to probability 0
      const lower = (s: string | undefined) => (s || '').toLowerCase();
      const lostStage = stages.find(s => ['lost', 'perdu'].includes(lower(s.name)))
        || stages.find(s => Math.round(s.probability) === 0);
      if (!lostStage) {
        throw new Error('Aucune étape "lost" trouvée pour ce pipeline');
      }
      // 4) Move the deal to the lost stage (backend will derive status + probability)
      const moveResp = await api.post(`/deals/${dealId}/move`, { stage_id: lostStage.id });
      return moveResp.data;
    },
    onSuccess: (data: Deal, dealId: number) => {
      // Mettre à jour le cache du deal
      queryClient.setQueryData(['deal', dealId], data);
      // Invalider les boards et la liste des deals
      queryClient.invalidateQueries(['deals']);
    },
  });
};
